import { useCallback, useEffect, useRef, useState } from "react";

/**
 * The DocuSeal builder is a `<docuseal-builder>` custom element, not an iframe: it
 * renders in this document's own shadow root and reports through `CustomEvent`s fired
 * on the element. There is no `postMessage` and no origin to check. What is checked
 * instead is that an event comes from the element this page mounted (the listeners
 * are bound to it and the events do not bubble) and carries this page's template id.
 */
const BUILDER_TAG = "docuseal-builder";
const SAVE_BUTTON_SELECTOR = "button.save-button";
/** The builder alerts and aborts on a failed save without emitting anything. */
export const BUILDER_SAVE_TIMEOUT_MS = 20_000;
/** Why `save` refuses a template with no fields (the builder itself would just alert). */
export const NO_FIELDS_MESSAGE = "The builder cannot save a template with no fields.";

/** The template the builder reports on its events. */
export interface BuilderTemplate {
  id?: number;
  name?: string;
  fields?: unknown[];
  schema?: unknown;
  submitters?: unknown;
  variables_schema?: unknown;
}

/** The `<docuseal-builder>` currently mounted under `host`, following remounts. */
export function useBuilderElement(host: HTMLElement | null): HTMLElement | null {
  const [element, setElement] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (!host) {
      setElement(null);
      return;
    }
    const sync = () => setElement(host.querySelector<HTMLElement>(BUILDER_TAG));
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(host, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [host]);
  return element;
}

/** The template an event carries, or null when it is not one of the builder's. */
export function templateFromEvent(event: Event): BuilderTemplate | null {
  const detail = (event as CustomEvent<unknown>).detail;
  return detail !== null && typeof detail === "object" ? (detail as BuilderTemplate) : null;
}

/**
 * What the builder saves: layout and fields. The name is left out because the page
 * renames the template itself (the builder's title is hidden), and a save that echoes
 * a normalised copy of the same content must still compare equal.
 */
export function builderSignature(template: BuilderTemplate): string {
  return JSON.stringify([
    template.schema,
    template.fields,
    template.submitters,
    template.variables_schema,
  ]);
}

/** The parts of a `builderSignature` other than the fields, so a fields-only change shows. */
function otherThanFields(signature: string | null): string | null {
  if (signature === null) return null;
  const [schema, , submitters, variables] = JSON.parse(signature) as unknown[];
  return JSON.stringify([schema, submitters, variables]);
}

interface PendingSave {
  /** The content the builder was asked to save, so edits made while it saves stay dirty. */
  signature: string | null;
  promise: Promise<BuilderTemplate>;
  resolve: (template: BuilderTemplate) => void;
  reject: (error: Error) => void;
}

interface BridgeState {
  /** null until the builder reports its loaded template. */
  baseline: string | null;
  current: string | null;
  template: BuilderTemplate | null;
  pending: PendingSave | null;
}

const freshState = (): BridgeState => ({
  baseline: null,
  current: null,
  template: null,
  pending: null,
});

interface BuilderBridgeOptions {
  /**
   * Events for any other template are ignored. `null` (a template the builder is about
   * to create) follows the first template the builder loads.
   */
  templateId: number | null;
  /** Every confirmed save. `external` is true when the builder's own Save was used. */
  onSave?: (template: BuilderTemplate, external: boolean) => void;
  /**
   * Saves a template with no fields, which the builder refuses to save itself (it alerts
   * and aborts). Without it such a save is rejected.
   */
  clearFields?: (templateId: number) => Promise<void>;
}

export interface BuilderBridge {
  /** The builder holds content its last confirmed save does not. */
  dirty: boolean;
  /** Counts the builder's content changes, including ones that leave `dirty` as it was. */
  revision: number;
  /** Presses the builder's Save and resolves once it confirms. Rejects, leaving `dirty` set, if it does not. */
  save: () => Promise<BuilderTemplate>;
}

/**
 * Mirrors the builder's dirty state to the page and lets the page's own Save drive the
 * builder's. `dirty` clears only on a save the builder confirmed, never on a click.
 */
export function useBuilderBridge(
  element: HTMLElement | null,
  { templateId, onSave, clearFields }: BuilderBridgeOptions
): BuilderBridge {
  const [dirty, setDirty] = useState(false);
  const [revision, setRevision] = useState(0);
  const state = useRef<BridgeState>(freshState());
  const onSaveRef = useRef(onSave);
  const clearFieldsRef = useRef(clearFields);
  useEffect(() => {
    onSaveRef.current = onSave;
    clearFieldsRef.current = clearFields;
  }, [onSave, clearFields]);

  useEffect(() => {
    if (!element) return;
    const s = freshState();
    state.current = s;
    setDirty(false);

    const refreshDirty = () => setDirty(s.baseline !== null && s.current !== s.baseline);
    let expectedId = templateId;
    const accept = (event: Event): BuilderTemplate | null => {
      const template = templateFromEvent(event);
      return template && template.id === expectedId ? template : null;
    };

    const onLoad = (event: Event) => {
      if (expectedId === null) expectedId = templateFromEvent(event)?.id ?? null;
      const template = accept(event);
      if (!template) return;
      s.template = template;
      s.baseline = s.current = builderSignature(template);
      refreshDirty();
    };
    const onChange = (event: Event) => {
      const template = accept(event);
      if (!template) return;
      s.template = template;
      s.current = builderSignature(template);
      refreshDirty();
      setRevision((count) => count + 1);
    };
    const onSaved = (event: Event) => {
      const template = accept(event);
      if (!template) return;
      const { pending } = s;
      s.pending = null;
      s.template = template;
      s.baseline = pending ? pending.signature : s.current;
      refreshDirty();
      pending?.resolve(template);
      onSaveRef.current?.(template, !pending);
    };

    element.addEventListener("load", onLoad);
    element.addEventListener("change", onChange);
    element.addEventListener("save", onSaved);
    return () => {
      element.removeEventListener("load", onLoad);
      element.removeEventListener("change", onChange);
      element.removeEventListener("save", onSaved);
      s.pending?.reject(new Error("The builder closed before the save finished."));
      s.pending = null;
    };
  }, [element, templateId]);

  const save = useCallback((): Promise<BuilderTemplate> => {
    const s = state.current;
    if (s.pending) return s.pending.promise;
    const button = element?.shadowRoot?.querySelector<HTMLButtonElement>(SAVE_BUTTON_SELECTOR);
    if (!button) return Promise.reject(new Error("The builder is not ready to save."));
    // The builder alerts and silently aborts when there is nothing to save, so a
    // template with no fields (sent as its raw PDF) is saved through the server.
    if (s.template?.fields?.length === 0) {
      const template = s.template;
      const clear = clearFieldsRef.current;
      if (template.id == null || !clear) {
        return Promise.reject(new Error(NO_FIELDS_MESSAGE));
      }
      // Clearing the fields saves nothing else: other unsaved changes would be lost.
      if (otherThanFields(s.current) !== otherThanFields(s.baseline)) {
        return Promise.reject(
          new Error("Save the template's other changes with at least one field first.")
        );
      }
      const signature = s.current;
      return clear(template.id).then(() => {
        s.baseline = signature;
        setDirty(s.current !== s.baseline);
        onSaveRef.current?.(template, false);
        return template;
      });
    }

    let resolve!: PendingSave["resolve"];
    let reject!: PendingSave["reject"];
    const promise = new Promise<BuilderTemplate>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    const timer = setTimeout(() => {
      if (s.pending?.promise === promise) s.pending = null;
      reject(new Error("The builder did not confirm the save."));
    }, BUILDER_SAVE_TIMEOUT_MS);
    s.pending = {
      signature: s.current,
      promise,
      resolve: (template) => {
        clearTimeout(timer);
        resolve(template);
      },
      reject: (error) => {
        clearTimeout(timer);
        reject(error);
      },
    };
    button.click();
    return promise;
  }, [element]);

  return { dirty, revision, save };
}
